/**
 * ModalProvider — Global context for opening modals imperatively.
 * Provides useModal() hook with openConfirm() for confirm dialogs.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ConfirmModal } from './ConfirmModal';
import type { ConfirmModalProps } from './types';

type ConfirmConfig = Omit<ConfirmModalProps, 'isOpen' | 'onClose' | 'isPending'>;

interface ModalContextValue {
  openConfirm: (config: ConfirmConfig) => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [confirmConfig, setConfirmConfig] = useState<ConfirmConfig | null>(null);
  const [isPending, setIsPending] = useState(false);

  const openConfirm = useCallback((config: ConfirmConfig) => {
    setConfirmConfig(config);
  }, []);

  const closeConfirm = useCallback(() => {
    setConfirmConfig(null);
    setIsPending(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!confirmConfig) return;
    try {
      setIsPending(true);
      await confirmConfig.onConfirm();
    } finally {
      closeConfirm();
    }
  }, [confirmConfig, closeConfirm]);

  return (
    <ModalContext.Provider value={{ openConfirm }}>
      {children}
      {confirmConfig && (
        <ConfirmModal
          isOpen={true}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
          title={confirmConfig.title}
          message={confirmConfig.message}
          variant={confirmConfig.variant}
          isPending={isPending}
          confirmLabel={confirmConfig.confirmLabel}
          cancelLabel={confirmConfig.cancelLabel}
        />
      )}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within <ModalProvider>');
  return ctx;
}
